import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { filterLocationIdsByACLs } from '@frontend/auth-helpers';
import { CountThreadsIO } from '../types';
import { useSMSDataV4Queries } from './use-sms-data-v4-queries';
import { UseSMSDataV4QueryEndpointArgs, useSMSDataV4Query } from './use-sms-data-v4-query';

/**
 * A hook that returns a query for the `CountThreads` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useCountThreadsQuery = <E = unknown, D = CountThreadsIO['output']>({
  request,
  ...args
}: UseSMSDataV4QueryEndpointArgs<'CountThreads', E, D>) => {
  return useSMSDataV4Query({
    endpointName: 'CountThreads',
    request: (context) => {
      const resolvedRequest = typeof request === 'function' ? request(context) : request;
      return {
        ...request,
        locationIds: filterLocationIdsByACLs({
          locationIds: resolvedRequest.locationIds,
          acls: Permission.SMS_INBOX_MANAGE,
        }),
      };
    },
    ...args,
  });
};

/**
 * A hook that returns an array of queries for the `CountThreads` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useCountThreadsQueries = <E = unknown, D = CountThreadsIO['output']>(
  argsArr: UseSMSDataV4QueryEndpointArgs<'CountThreads', E, D>[]
) =>
  useSMSDataV4Queries(
    argsArr.map<Parameters<typeof useSMSDataV4Queries<'CountThreads', E, D>>[0][number]>(({ request, ...args }) => ({
      endpointName: 'CountThreads',
      request: (context) => {
        const resolvedRequest = typeof request === 'function' ? request(context) : request;
        return {
          ...request,
          locationIds: filterLocationIdsByACLs({
            locationIds: resolvedRequest.locationIds,
            acls: Permission.SMS_INBOX_MANAGE,
          }),
        };
      },
      ...args,
    }))
  );
